import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Smartphone, ChevronRight, ChevronLeft, Check, HelpCircle,
  Calculator, Clock, Shield, Phone, ArrowRight, AlertTriangle, Wrench
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SellPhoneWidget = () => {
  const [step, setStep] = useState(1); // 1: Select Device, 2: QC Questions, 3: Quote
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [qcConfig, setQcConfig] = useState(null);
  const [responses, setResponses] = useState({});
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  useEffect(() => {
    loadBrands();
    loadCQCConfig();
  }, []);

  const loadBrands = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/brands`);
      // Handle both array and {brands: [...]} response formats
      const brandsData = response.data.brands || response.data || [];
      // Filter to phone brands only
      const phoneBrands = brandsData.filter(b => 
        ['Apple', 'Samsung', 'OnePlus', 'Xiaomi', 'Vivo', 'Oppo', 'Realme', 'Google', 'Nothing', 'Motorola', 'Nokia', 'IQOO', 'Poco', 'Iqoo'].includes(b.name)
      );
      setBrands(phoneBrands);
    } catch (error) {
      console.error('Failed to load brands:', error);
    }
  };

  const loadCQCConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/qc-stage/cqc/config`);
      setQcConfig(response.data);
    } catch (error) {
      console.error('Failed to load CQC config:', error);
    }
  };

  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setProducts([]);
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/products`, {
        params: { search: query, category: 'Mobile Phones', limit: 20 }
      });
      setProducts(response.data.products || response.data || []);
    } catch (error) {
      console.error('Failed to search products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProductsByBrand = async (brandName) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/products`, {
        params: { brand: brandName, category: 'Mobile Phones', limit: 50 }
      });
      setProducts(response.data.products || response.data || []);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectProduct = (product) => {
    setSelectedProduct(product);
    setSelectedVariant(null);
    if (product.variants && product.variants.length > 0) {
      // Stay on step 1 for variant selection
    } else {
      setStep(2);
    }
  };

  const selectVariant = (variant) => {
    setSelectedVariant(variant);
    setStep(2);
  };

  const handleResponse = (paramId, optionId) => {
    setResponses(prev => ({
      ...prev,
      [paramId]: optionId
    }));
  };

  const getQuote = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/qc-stage/cqc/quote`, {
        product_id: selectedProduct.sku_code || selectedProduct._id,
        variant_id: selectedVariant?.zupc_code,
        responses: responses
      });
      setQuote(response.data);
      setStep(3);
    } catch (error) {
      console.error('Failed to get quote:', error);
      alert(error.response?.data?.detail || 'Failed to generate quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Group parameters by category for display
  const groupedParams = qcConfig?.parameters?.filter(p => p.enabled).reduce((acc, param) => {
    const cat = param.category_name || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(param);
    return acc;
  }, {}) || {};

  const enabledParams = qcConfig?.parameters?.filter(p => p.enabled).sort((a, b) => a.order - b.order) || [];
  const answeredCount = Object.keys(responses).length;
  const totalQuestions = enabledParams.length;
  const progress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  const canGetQuote = answeredCount >= Math.ceil(totalQuestions * 0.5); // 50% questions answered

  const getGradeColor = (grade) => {
    const colors = {
      Z1: 'bg-green-500', Z2: 'bg-green-400', Z3: 'bg-lime-500',
      Z4: 'bg-yellow-500', Z5: 'bg-orange-400', Z6: 'bg-orange-500', Z7: 'bg-red-500'
    };
    return colors[grade] || 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#2b3380' }}>
                <Smartphone className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Sell Your Phone</h1>
                <p className="text-sm text-gray-500">Get instant price quote</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Shield className="h-4 w-4 text-green-500" />
                <span>Safe & Secure</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4 text-blue-500" />
                <span>Instant Quote</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center gap-4 mb-8">
          {[
            { num: 1, label: 'Select Device' },
            { num: 2, label: 'Device Condition' },
            { num: 3, label: 'Get Quote' }
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className={`flex items-center gap-2 ${step >= s.num ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step > s.num ? 'bg-green-500 text-white' :
                  step === s.num ? 'bg-blue-600 text-white' : 'bg-gray-200'
                }`}>
                  {step > s.num ? <Check className="h-4 w-4" /> : s.num}
                </div>
                <span className="hidden sm:inline text-sm font-medium">{s.label}</span>
              </div>
              {idx < 2 && <ChevronRight className="h-4 w-4 text-gray-300" />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Select Device */}
        {step === 1 && (
          <div className="bg-white rounded-xl shadow-lg p-6" data-testid="sell-phone-step1">
            <h2 className="text-xl font-semibold mb-4">Which phone do you want to sell?</h2>
            
            {/* Search */}
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search by phone name (e.g., iPhone 15 Pro Max)"
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchProducts(e.target.value);
                }}
                data-testid="phone-search-input"
              />
            </div>

            {/* Brand Selection */}
            {!searchQuery && !selectedBrand && (
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-3">Or select brand</h3>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                  {brands.map(brand => (
                    <button
                      key={brand._id || brand.name}
                      onClick={() => {
                        setSelectedBrand(brand);
                        loadProductsByBrand(brand.name);
                      }}
                      className="p-3 border rounded-lg hover:border-blue-500 hover:bg-blue-50 flex flex-col items-center gap-2"
                    >
                      <img 
                        src={brand.logo_url || `https://ui-avatars.com/api/?name=${brand.name}&background=random`} 
                        alt={brand.name}
                        className="w-10 h-10 object-contain"
                      />
                      <span className="text-xs font-medium">{brand.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Back to brands */}
            {selectedBrand && !selectedProduct && (
              <button
                onClick={() => {
                  setSelectedBrand(null);
                  setProducts([]);
                }}
                className="flex items-center gap-1 text-sm text-blue-600 mb-4 hover:underline"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to brands
              </button>
            )}

            {/* Product List */}
            {(searchQuery || selectedBrand) && products.length > 0 && !selectedProduct && (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {products.map(product => (
                  <button
                    key={product._id || product.sku_code}
                    onClick={() => selectProduct(product)}
                    className="w-full p-4 border rounded-lg hover:border-blue-500 hover:bg-blue-50 flex items-center gap-4 text-left"
                    data-testid={`product-${product.sku_code}`}
                  >
                    <img 
                      src={product.images?.[0] || product.image_url || 'https://via.placeholder.com/60'} 
                      alt={product.model}
                      className="w-16 h-16 object-contain"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{product.brand} {product.model}</div>
                      <div className="text-sm text-gray-500">{product.category}</div>
                      {product.our_mbp > 0 && (
                        <div className="text-sm text-green-600 font-medium">Up to ₹{product.our_mbp?.toLocaleString()}</div>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </button>
                ))}
              </div>
            )}

            {/* Variant Selection */}
            {selectedProduct && !selectedVariant && selectedProduct.variants?.length > 0 && (
              <div>
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="flex items-center gap-1 text-sm text-blue-600 mb-4 hover:underline"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back to models
                </button>
                <div className="flex items-center gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                  <img 
                    src={selectedProduct.images?.[0] || 'https://via.placeholder.com/60'} 
                    alt={selectedProduct.model}
                    className="w-16 h-16 object-contain"
                  />
                  <div>
                    <div className="font-medium">{selectedProduct.brand} {selectedProduct.model}</div>
                    <div className="text-sm text-gray-500">Select your variant</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {selectedProduct.variants.map(variant => (
                    <button
                      key={variant.zupc_code || variant._id}
                      onClick={() => selectVariant(variant)}
                      className="p-4 border rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left"
                    >
                      <div className="font-medium">{variant.storage || variant.name}</div>
                      {variant.ram && <div className="text-sm text-gray-500">{variant.ram} RAM</div>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No variants - direct selection */}
            {selectedProduct && (!selectedProduct.variants || selectedProduct.variants.length === 0) && (
              <div className="text-center py-4">
                <p>Selected: {selectedProduct.brand} {selectedProduct.model}</p>
                <button
                  onClick={() => setStep(2)}
                  className="mt-4 px-6 py-2 rounded-lg text-white"
                  style={{ backgroundColor: '#2b3380' }}
                >
                  Continue
                </button>
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: QC Questions - Clean List View */}
        {step === 2 && (
          <div className="bg-white rounded-xl shadow-lg p-6" data-testid="sell-phone-step2">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold">Tell us about your phone</h2>
                <p className="text-sm text-gray-500">
                  {selectedProduct?.brand} {selectedProduct?.model}
                  {selectedVariant && ` - ${selectedVariant.storage}`}
                </p>
              </div>
              <button
                onClick={() => {
                  setStep(1);
                  setResponses({});
                }}
                className="text-sm text-blue-600 hover:underline"
              >
                Change Device
              </button>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>{answeredCount} of {totalQuestions} questions answered</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            {/* Questions by Category */}
            <div className="space-y-8 max-h-[500px] overflow-y-auto">
              {Object.entries(groupedParams).map(([category, params]) => (
                <div key={category}>
                  <h3 className="font-semibold text-gray-700 mb-4 pb-2 border-b">{category}</h3>
                  <div className="space-y-6">
                    {params.map((param, idx) => (
                      <div key={param.parameter_id} className="pb-4">
                        <div className="flex items-start gap-2 mb-3">
                          <label className="font-medium text-gray-800">
                            {param.simplified_label || param.original_name}
                          </label>
                          {param.help_text && (
                            <span className="text-gray-400 cursor-help" title={param.help_text}>
                              <HelpCircle className="h-4 w-4" />
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {param.simplified_options?.map(opt => (
                            <button
                              key={opt.id}
                              onClick={() => handleResponse(param.parameter_id, opt.id)}
                              className={`p-3 rounded-lg border text-sm text-left transition-all ${
                                responses[param.parameter_id] === opt.id
                                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                  : 'hover:border-gray-300 hover:bg-gray-50'
                              }`}
                              data-testid={`option-${param.parameter_id}-${opt.id}`}
                            >
                              <div className="flex items-center gap-2">
                                {responses[param.parameter_id] === opt.id && (
                                  <Check className="h-4 w-4 text-blue-600" />
                                )}
                                <span>{opt.label}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Get Quote Button */}
            <div className="mt-6 pt-4 border-t">
              <button
                onClick={getQuote}
                disabled={!canGetQuote || loading}
                className="w-full py-3 rounded-lg text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#2b3380' }}
                data-testid="get-quote-btn"
              >
                {loading ? (
                  <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                ) : (
                  <>
                    <Calculator className="h-5 w-5" />
                    Get Instant Quote
                  </>
                )}
              </button>
              {!canGetQuote && (
                <p className="text-center text-sm text-gray-500 mt-2">
                  Please answer at least {Math.ceil(totalQuestions * 0.5)} questions
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Quote - Enhanced with Real Price Engine Data */}
        {step === 3 && quote && (
          <div className="bg-white rounded-xl shadow-lg p-6" data-testid="sell-phone-step3">
            <div className="text-center mb-6">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                quote.is_salvage ? 'bg-red-100' : 'bg-green-100'
              }`}>
                {quote.is_salvage ? (
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                ) : (
                  <Check className="h-8 w-8 text-green-600" />
                )}
              </div>
              <h2 className="text-xl font-semibold">Your Price Quote</h2>
              <p className="text-sm text-gray-500">
                {quote.product_name || `${selectedProduct?.brand} ${selectedProduct?.model}`}
                {selectedVariant && ` - ${selectedVariant.storage}`}
              </p>
            </div>

            {/* Price Card */}
            <div className={`rounded-xl p-6 text-white text-center mb-6 ${
              quote.is_salvage 
                ? 'bg-gradient-to-r from-red-600 to-red-700' 
                : 'bg-gradient-to-r from-blue-600 to-indigo-600'
            }`}>
              <div className="text-sm opacity-80 mb-1">Estimated Value</div>
              <div className="text-4xl font-bold">₹{(quote.indicative_price || quote.final_price || 0).toLocaleString()}</div>
              <div className="flex items-center justify-center gap-4 mt-3">
                {quote.grade && (
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${getGradeColor(quote.grade)}`}>
                    Grade: {quote.grade}
                  </span>
                )}
                <span className="text-sm opacity-80">
                  Quote ID: {quote.quote_id}
                </span>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium mb-3">Price Breakdown</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Maximum Buyback Price (MBP)</span>
                  <span>₹{(quote.mbp || quote.base_price || 0).toLocaleString()}</span>
                </div>
                
                {/* Deductions */}
                {quote.deduction_breakdown?.length > 0 && quote.deduction_breakdown.slice(0, 5).map((d, idx) => (
                  <div key={idx} className="flex justify-between text-red-600">
                    <span className="truncate max-w-[200px]">{d.parameter}: {d.response}</span>
                    <span>-₹{(d.deduction_amount || 0).toLocaleString()}</span>
                  </div>
                ))}
                {quote.deduction_breakdown?.length > 5 && (
                  <div className="text-gray-500 text-xs">+{quote.deduction_breakdown.length - 5} more deductions</div>
                )}
                
                {quote.total_deduction > 0 && (
                  <div className="flex justify-between text-red-600 font-medium pt-2 border-t">
                    <span>Total Deductions</span>
                    <span>-₹{(quote.total_deduction || 0).toLocaleString()}</span>
                  </div>
                )}
                
                {/* Top-ups */}
                {quote.topup_breakdown?.length > 0 && quote.topup_breakdown.map((t, idx) => (
                  <div key={idx} className="flex justify-between text-green-600">
                    <span>{t.item}: {t.option}</span>
                    <span>+₹{(t.amount || 0).toLocaleString()}</span>
                  </div>
                ))}
                
                {quote.total_topup > 0 && (
                  <div className="flex justify-between text-green-600 font-medium">
                    <span>Total Top-ups</span>
                    <span>+₹{(quote.total_topup || 0).toLocaleString()}</span>
                  </div>
                )}
                
                <div className="flex justify-between font-bold pt-2 border-t">
                  <span>Final Quote</span>
                  <span className="text-green-600">₹{(quote.indicative_price || quote.final_price || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Issues Found */}
            {quote.issues_found?.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                  <Wrench className="h-4 w-4" />
                  Issues Detected ({quote.issues_found.length})
                </h4>
                <ul className="text-sm text-orange-700 space-y-1">
                  {quote.issues_found.slice(0, 3).map((issue, idx) => (
                    <li key={idx}>• {issue.parameter}: {issue.issue}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Disclaimer */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-sm text-yellow-800">
              <strong>Note:</strong> This is an indicative price based on your inputs. 
              Final price will be confirmed after physical inspection by our engineer.
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <button
                className="w-full py-3 rounded-lg text-white font-medium flex items-center justify-center gap-2"
                style={{ backgroundColor: '#2b3380' }}
                data-testid="schedule-pickup-btn"
              >
                <Phone className="h-5 w-5" />
                Schedule Pickup
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  setStep(1);
                  setSelectedProduct(null);
                  setSelectedVariant(null);
                  setResponses({});
                  setQuote(null);
                }}
                className="w-full py-3 rounded-lg border font-medium hover:bg-gray-50"
              >
                Get Quote for Another Device
              </button>
            </div>

            <p className="text-center text-xs text-gray-400 mt-4">
              Quote valid until: {new Date(quote.valid_until).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SellPhoneWidget;
